import type { ShipmentSummary } from '~api/som_search';

import { Fragment, ReactNode, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { DropdownMenu, InteractiveTable, Modal, Text } from '@ingrid/luterkort-react';

import { RoleGuard } from '~src/components/RoleGuard/RoleGuard';
import { IgnoredError } from '~src/errors/IgnoredError';
import { useTranslation } from '~src/i18n/use-translation';
import { useSiteId } from '~src/main/hooks/useSiteId';
import { useToast } from '~src/main/hooks/useToast';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';
import { AppRoute } from '~src/main/routes';
import { extractFileName } from '~src/utils/file';
import { isCancelFailedResponse } from '~src/utils/status-helpers';

import { useShipmentActions } from '../hooks';
import { useFetchShippingLabelsMutation, useGenerateLabelsUrlMutation, useShipmentCancelMutation } from '../mutations';
import { useShipmentMetadataPolling } from '../polling';
import { FreightDocumentDialog } from './freight-document-dialog';

interface Props {
  shipment: Pick<ShipmentSummary, 'id' | 'status' | 'direction_type'>;
  hideShowDetails?: boolean;
  trigger: ReactNode;
}

export function Menu({ shipment, trigger, hideShowDetails = false }: Props) {
  const [isFreightDocumentsModalOpen, setIsFreightDocumentsModalOpen] = useState(false);

  const fetchLabelsMutation = useFetchShippingLabelsMutation();
  const urlMutation = useGenerateLabelsUrlMutation();
  const cancelMutation = useShipmentCancelMutation();

  const actions = useShipmentActions({ shipment, exclude: hideShowDetails ? ['SHOW_DETAILS'] : [] });
  const navigate = useNavigate();
  const tracking = useTracking();

  const { setHighlightedKey } = InteractiveTable.useContext();
  const siteId = useSiteId();
  const { t } = useTranslation();
  const { toast } = useToast();

  const shipmentMetadataPolling = useShipmentMetadataPolling();

  const cancelShipment = async () => {
    const id = shipment.id;

    const promise = async () => {
      try {
        await cancelMutation.mutateAsync({ shipment_id: id });
        tracking.capture(TrackingEventEnum.ShipmentCancelCompleted, { shipmentId: id });

        await shipmentMetadataPolling.mutateAsync({
          pollUntil: data => data?.status === 'CANCELED',
          variables: id,
        });
      } catch (e) {
        tracking.capture(TrackingEventEnum.ShipmentCancelFailed, { shipmentId: id });

        if (isCancelFailedResponse(e)) {
          await shipmentMetadataPolling.mutateAsync({
            pollUntil: data => data?.status === 'CANCEL_FAILED',
            variables: id,
          });

          return Promise.reject(new IgnoredError('failed to cancel'));
        }

        throw e;
      }

      setHighlightedKey(null);
    };

    await toast.promise(promise, {
      loading: { title: t('shipments.details.cancelLoading') },
      error: { title: t('shipments.details.cancelError') },
      success: { title: t('shipments.details.cancelSuccess') },
    });
  };

  const cancelAndDuplicate = async () => {
    const promise = cancelMutation.mutateAsync({ shipment_id: shipment.id });

    await toast.promise(promise, {
      loading: { title: t('shipments.details.cancelLoading') },
      error: { title: t('shipments.details.cancelError') },
      success: { title: t('shipments.details.cancelSuccess') },
    });

    navigate(`${AppRoute.DUPLICATE}/${shipment.id}`);
  };

  const printLabels = async () => {
    const promise = async () => {
      const {
        data: { labels_urls },
      } = await fetchLabelsMutation.mutateAsync({ shipment_ids: [shipment.id!] });

      if (!labels_urls?.length) {
        throw new IgnoredError(`failed to print label for shipment #${shipment.id}`);
      }

      const {
        data: { url },
      } = await urlMutation.mutateAsync({ file_name: extractFileName(labels_urls[0]) });

      window.open(url);
    };

    await toast.promise(promise, {
      loading: { title: t('shipment.details.printLoading') },
      success: { title: t('shipment.details.printSuccess') },
      error: { title: t('shipment.details.printError') },
    });
  };

  // hack, pointer-events: none isn't being removed when closing
  useEffect(() => {
    if (!isFreightDocumentsModalOpen) {
      setTimeout(() => (document.body.style.pointerEvents = 'auto'));
    }
  }, [isFreightDocumentsModalOpen]);

  if (!actions.length) {
    return null;
  }

  return (
    <Fragment>
      <Modal.Root open={isFreightDocumentsModalOpen} onOpenChange={setIsFreightDocumentsModalOpen}>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>{trigger}</DropdownMenu.Trigger>

          <DropdownMenu.Content>
            {actions.includes('SHOW_DETAILS') && (
              <DropdownMenu.Item asChild>
                <RouterLink to={`/${AppRoute.SITE}/${siteId}/${AppRoute.SHIPMENTS}/${shipment.id}`}>
                  {t('common.showDetails')}
                </RouterLink>
              </DropdownMenu.Item>
            )}

            {actions.includes('EDIT') && (
              <RoleGuard roles={['transport:edit']} withTooltip>
                <DropdownMenu.Item asChild>
                  <RouterLink to={`${AppRoute.EDIT}/${shipment.id}`}>{t('common.edit')}</RouterLink>
                </DropdownMenu.Item>
              </RoleGuard>
            )}

            {actions.includes('FREIGHT_DOCUMENTS') && (
              <RoleGuard roles={['transport:edit']} withTooltip>
                <DropdownMenu.Item onClick={() => setIsFreightDocumentsModalOpen(true)}>
                  {t('common.sendFreightDocuments')}
                </DropdownMenu.Item>
              </RoleGuard>
            )}

            {actions.includes('PRINT_LABEL') && (
              <DropdownMenu.Item onClick={printLabels}>{t('common.printLabels')}</DropdownMenu.Item>
            )}

            {actions.includes('CREATE_RETURN') && (
              <RoleGuard roles={['transport:edit']} withTooltip>
                <DropdownMenu.Item asChild>
                  <RouterLink to={`${AppRoute.RETURN}/${shipment.id}`}>{t('common.createReturn')}</RouterLink>
                </DropdownMenu.Item>
              </RoleGuard>
            )}

            {actions.includes('DUPLICATE') && (
              <RoleGuard roles={['transport:edit']} withTooltip>
                <DropdownMenu.Item asChild>
                  <RouterLink to={`${AppRoute.DUPLICATE}/${shipment.id}`}>{t('common.duplicate')}</RouterLink>
                </DropdownMenu.Item>
              </RoleGuard>
            )}

            {actions.includes('CANCEL_AND_DUPLICATE') && (
              <RoleGuard roles={['transport:edit']} withTooltip>
                <DropdownMenu.Item onClick={cancelAndDuplicate}>{t('common.cancelAndDuplicate')}</DropdownMenu.Item>
              </RoleGuard>
            )}

            {actions.includes('CANCEL') && (
              <RoleGuard roles={['transport:edit']} withTooltip>
                <DropdownMenu.Item onClick={cancelShipment}>
                  <Text variant="critical">{t('common.cancel')}</Text>
                </DropdownMenu.Item>
              </RoleGuard>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        <Modal.Portal>
          <FreightDocumentDialog shipmentId={shipment.id} onSuccess={() => setIsFreightDocumentsModalOpen(false)} />
        </Modal.Portal>
      </Modal.Root>
    </Fragment>
  );
}
